import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Inbox } from "lucide-react";

export default function AdminSupport() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const selRef = useRef(selected);
  selRef.current = selected;

  const loadConvos = useCallback(async () => {
    try {
      const { data } = await api.get("/support/conversations");
      setConvos(data);
    } catch {}
  }, []);

  const loadThread = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/support/messages?client_id=${cid}`);
      setMessages(data);
    } catch {}
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  useEffect(() => {
    const t = setInterval(() => {
      loadConvos();
      if (selRef.current) loadThread(selRef.current);
    }, 10000);
    return () => clearInterval(t);
  }, [loadConvos, loadThread]);

  // Live pushes relayed from Layout's SSE stream
  useEffect(() => {
    const onMsg = (e) => {
      if (e.detail?.client_id === selRef.current) {
        setMessages((m) => [...m, e.detail]);
      }
      loadConvos();
    };
    window.addEventListener("support-message", onMsg);
    return () => window.removeEventListener("support-message", onMsg);
  }, [loadConvos]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openThread = (cid) => {
    setSelected(cid);
    loadThread(cid);
    setConvos((cs) => cs.map((c) => (c.client_id === cid ? { ...c, unread: 0 } : c)));
  };

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || !selected || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/messages", { text: body, client_id: selected });
      setMessages((m) => [...m, data]);
      setText("");
      loadConvos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  };

  const current = convos.find((c) => c.client_id === selected);

  return (
    <div className="space-y-6" data-testid="support-inbox-page">
      <div>
        <div className="hs-overline text-blue-700">SUPPORT · LIVE CHAT</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Support Inbox</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">Client messages from the in-portal support chat. Replies appear instantly in their portal.</p>
      </div>

      <div className="hs-card grid lg:grid-cols-[320px_1fr] min-h-[560px]">
        {/* Conversation list */}
        <div className="border-r border-slate-200 overflow-y-auto max-h-[640px]">
          <div className="hs-overline px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Inbox size={12} /> CONVERSATIONS ({convos.length})
          </div>
          {convos.length === 0 && (
            <div className="p-6 text-xs text-slate-500" data-testid="support-no-conversations">
              No conversations yet. When a client writes in their portal chat, it shows up here (you also get an email alert).
            </div>
          )}
          {convos.map((c) => (
            <button
              key={c.client_id}
              onClick={() => openThread(c.client_id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected === c.client_id ? "bg-blue-50 border-l-2 border-l-blue-700" : ""}`}
              data-testid={`support-conversation-${c.client_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold truncate">{c.company_name || c.client_name || c.client_email}</span>
                {c.unread > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 bg-blue-700 text-white text-[10px] font-bold flex items-center justify-center" data-testid="support-convo-unread">
                    {c.unread}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">
                {c.last_sender_role !== "client" ? "You: " : ""}{c.last_message_preview}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {c.last_message_at ? new Date(c.last_message_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
              </div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="flex flex-col max-h-[640px]">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2">
              <MessageCircle size={32} />
              <div className="text-sm">Select a conversation to reply</div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="text-sm font-bold">{current?.company_name || current?.client_name}</div>
                <div className="text-xs text-slate-500">{current?.client_email}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="support-thread">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[70%] px-3 py-2 text-sm leading-snug ${
                      m.sender_role === "client"
                        ? "bg-white border border-slate-200 text-slate-800"
                        : "bg-blue-700 text-white"
                    }`}>
                      <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${m.sender_role === "client" ? "text-slate-400" : "text-blue-200"}`}>
                        {m.sender_role === "client" ? m.sender_name : `${m.sender_name} (team)`}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[9px] mt-1 ${m.sender_role === "client" ? "text-slate-400" : "text-blue-200"}`}>
                        {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
                <input
                  className="hs-input flex-1"
                  placeholder="Type your reply…"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  data-testid="support-reply-input"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="hs-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="support-reply-send"
                >
                  <Send size={14} /> SEND
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
